// Hook to fetch/put/post/delete the /application response

/*  Shared  */
import { Helpers, Notify, Response } from 'application/Shared/Helpers';
import { debug } from 'application/Shared/Utils';
import { Application } from 'application/Shared/types/application';

/*  NPM  */
import axios, { AxiosError } from 'axios';
import { TFunction } from 'i18next';

/*  React  */
import { useCallback, useEffect, useRef, useState } from 'react';

export const useFetchApplications = (applicationId: string[] | string) => {
    const [application, setApplication] = useState<Application[] | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const { CancelToken } = axios;
        const source = CancelToken.source();

        const fetchData = async () => {
            setLoading(true);
            if (!applicationId.length) {
                setLoading(false);
                return;
            }
            try {
                const response = await axios.get(
                    `/application/${applicationId}`,
                    {
                        cancelToken: source.token,
                        params: { size: -1 }
                    }
                );
                if (!Response.hasData(response)) {
                    debug.e('Unable to get application. No data.');
                } else {
                    setApplication(Response.getData(response));
                }
            } catch (error) {
                debug.e('Unable to get applications.', error);
            }
            setLoading(false);
        };

        fetchData();

        return () => {
            // Cancel the request when the component unmounts
            source.cancel();
        };
    }, [applicationId]);

    return {
        application,
        loading
    };
};

export const useFetchApplicationByParams = (
    params: Record<string, string | number | boolean>
) => {
    const [application, setApplication] = useState({});
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const { CancelToken } = axios;
        const source = CancelToken.source();

        const fetchData = async () => {
            setLoading(true);
            if (Helpers.isEmpty(params)) {
                setLoading(false);
                return;
            }
            try {
                const response = await axios.get(`/application`, {
                    cancelToken: source.token,
                    params
                });
                if (!Response.hasData(response)) {
                    debug.e('Unable to get application. No data.');
                } else {
                    setApplication(Response.getOne(response));
                }
            } catch (error) {
                debug.e('Unable to get applications.', error);
            }
            setLoading(false);
        };

        fetchData();

        return () => {
            // Cancel the request when the component unmounts
            source.cancel();
        };
    }, [params]);

    return {
        application,
        loading
    };
};

// Hook to fetch the /application data async
export const useFetchApplicationAsync = () => {
    const error = useRef<AxiosError | null>(null);
    const application = useRef<Application | null>(null);

    const getApplication = useCallback(
        async (applicationId: string | number) => {
            const { CancelToken } = axios;
            const source = CancelToken.source();

            try {
                const response = await axios.get(
                    `/application/${applicationId}`,
                    {
                        cancelToken: source.token
                    }
                );
                if (!Response.hasData(response)) {
                    debug.e('Unable to get application. No data.');
                } else {
                    application.current = Response.getOne(response);
                }
            } catch (err) {
                error.current = err as AxiosError;
                debug.e('Error getting application information.', err);
            }
            return {
                application: application.current,
                error: error.current
            };
        },
        []
    );
    return { getApplication };
};

export const usePutApplication = (t: TFunction<'translation', undefined>) => {
    const [saving, setSaving] = useState(false);

    const putApplication = useCallback(
        async ({
            applicationId,
            params
        }: {
            applicationId: string | number;
            params: Partial<Application> & {
                forceSynchronousPut?: boolean;
                iconUpload?: unknown;
                imageUpload?: unknown;
            };
        }) => {
            try {
                setSaving(true);
                await axios.put(`/application/${applicationId}`, params);
                Notify.success(t('GlobalAdmin.Applications.appSettingsSaved'));
                setSaving(false);
                return true; // success = true
            } catch (err) {
                debug.e(t('GlobalAdmin.Applications.appSettingsNotSaved'), err);
                Notify.error(t('GlobalAdmin.Applications.appSettingsNotSaved'));
                setSaving(false);
                return false; // success = false
            }
        },
        [t]
    );

    return { putApplication, saving };
};

export const usePostApplication = (t: TFunction<'translation', undefined>) => {
    const [saving, setSaving] = useState(false);

    const postApplication = useCallback(
        async ({ baseName }: { baseName: string }) => {
            try {
                setSaving(true);
                const params = {
                    baseName,
                    forceBase: true
                };
                const response = await axios.post('/application/', params);
                Notify.success(t('GlobalAdmin.Application.added'));
                const app = Response.getId(response);
                setSaving(false);
                return app;
            } catch (err) {
                Notify.error(t('GlobalAdmin.Application.notAdded'));
                setSaving(false);
                return null;
            }
        },
        [t]
    );

    return { postApplication, saving };
};

export const useDeleteApplication = (
    t: TFunction<'translation', undefined>
) => {
    const [saving, setSaving] = useState(false);

    const deleteApplication = useCallback(
        async ({ applicationId }: { applicationId: string | number }) => {
            try {
                setSaving(true);
                await axios.delete(`/application/${applicationId}`);
                Notify.success(t('GlobalAdmin.Application.deleted'));
                setSaving(false);
                return true; // success = true
            } catch (err) {
                Notify.error(t('GlobalAdmin.Application.notDeleted'));
                setSaving(false);
                return false; // success = true
            }
        },
        [t]
    );

    return { deleteApplication, saving };
};
